import { supabase } from '../lib/supabase';

export type InterviewType = 'in_person' | 'video' | 'phone';

export interface Meeting {
  id: string;
  application_id: string;
  organizer_id: string;
  participant_user_id: string;
  start_at: string; // ISO
  end_at: string | null;
  duration_minutes: number | null;
  timezone: string;
  interview_type: InterviewType;
  location_address: string | null;
  meeting_link: string | null;
  notes: string | null;
  metadata: Record<string, any> | null;
  documents: string[]; // Array of document IDs
  status: 'draft' | 'proposed' | 'sent' | 'confirmed' | 'rescheduled' | 'cancelled' | 'completed';
  created_at: string;
  updated_at: string;
}

export interface CreateMeetingInput {
  applicationId: string;
  participantUserId: string;
  interviewType: InterviewType;
  timezone: string;
  localDateISO: string; // date part in selected timezone (YYYY-MM-DD)
  localTimeHHmm: string; // time part (HH:mm)
  durationMinutes?: number; // optional - default 60
  locationAddress?: string;
  meetingLink?: string;
  phoneNumber?: string;
  notes?: string;
  metadata?: Record<string, any>;
  documents?: string[]; // Array of document IDs
}

function toUtcFromLocal(dateISO: string, timeHHmm: string, timezone: string): string {
  // Create an ISO string representing the local datetime in the provided timezone then convert to UTC
  // We rely on Intl for formatting then parse back. For robust behavior consider server-side conversion.
  const [h, m] = timeHHmm.split(':').map(Number);
  const local = new Date(`${dateISO}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`);
  // Use the target timezone to compute the equivalent UTC by adjusting with the offset at that moment
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  });
  const parts = fmt.formatToParts(local).reduce<Record<string,string>>((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  const zonedString = `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`;
  const zonedDate = new Date(zonedString + 'Z'); // interpret as UTC-equivalent instant
  return zonedDate.toISOString();
}

export class MeetingService {
  static async createMeeting(input: CreateMeetingInput): Promise<Meeting> {
    const { data: auth } = await supabase.auth.getUser();
    const organizerId = auth.user?.id;
    if (!organizerId) throw new Error('Not authenticated');

    const duration = input.durationMinutes ?? 60;
    const startUtc = toUtcFromLocal(input.localDateISO, input.localTimeHHmm, input.timezone);
    const endUtc = new Date(new Date(startUtc).getTime() + duration * 60_000).toISOString();

    const payload = {
      application_id: input.applicationId,
      organizer_id: organizerId,
      participant_user_id: input.participantUserId,
      start_at: startUtc,
      end_at: endUtc,
      duration_minutes: duration,
      timezone: input.timezone,
      interview_type: input.interviewType,
      location_address: input.locationAddress ?? null,
      meeting_link: input.meetingLink ?? null,
      phone_number: input.phoneNumber ?? null,
      notes: input.notes ?? null,
      metadata: input.metadata ?? {},
      documents: input.documents ?? [],
      status: 'proposed' as const,
    };

    console.log('🔍 MeetingService: Creating meeting with payload:', payload);
    console.log('📄 Documents being saved:', input.documents);

    const { data, error } = await supabase
      .from('meetings')
      .insert(payload)
      .select('*')
      .single();

    if (error) {
      console.error('❌ MeetingService: Error creating meeting:', error);
      throw error;
    }

    console.log('✅ MeetingService: Meeting created successfully:', data);
    return data as Meeting;
  }

  static async getMeetingsForApplication(applicationId: string): Promise<Meeting[]> {
    const { data, error } = await supabase
      .from('meetings')
      .select('*')
      .eq('application_id', applicationId)
      .order('start_at', { ascending: true });
    if (error) throw error;
    return (data ?? []) as Meeting[];
  }

  static async updateMeetingStatus(meetingId: string, status: Meeting['status']): Promise<void> {
    const { error } = await supabase
      .from('meetings')
      .update({ status })
      .eq('id', meetingId);
    if (error) throw error;
  }
}


